import {
  reactive, ref, toRefs, watch, computed,
} from '@vue/composition-api';

import ParamsLocalStorage from '@/js/utils/ParamsLocalStorage';

import debounce from 'lodash/debounce';
import apiRequest from '@/js/helpers/apiRequest';

import { opened, closed } from '@/js/utils/modals/openedClosedModals';

export const SEARCH_HISTORY = 'searchHistory';
export const SEARCH_URL_PART = '/search/?q=';

export default (props) => {
  const state = reactive({
    suggestsList: [],
    query: props.dataset.query || '',
    popular: [],
    history: [],
    loading: false,
    showError: false,
    showSuggests: false,
    isQueryAlreadyExist: false,
    currentFocusEl: 0,
    currentLink: '',
    currentLinkTitle: '',
    disableHideSuggestions: false,
  });

  const input = ref(null);

  const combinedPopularHistory = computed(() => [
    ...state.popular,
    ...state.history.map(item => ({ PHRASE: item })),
  ]);

  const trimQuery = value => value.replace(/ +(?= )/g, '').trim();

  const moveResults = (e) => {
    const elements = document.querySelectorAll('.js-search-results .js-suggest-item');
    const getItem = (count, item) => {
      if (state.suggestsList[count]) {
        state.currentLink = state.suggestsList[count].link;
        state.currentLinkTitle = state.suggestsList[count].title;
        return false;
      } if (item && item.classList.includes('js-suggest-item')) {
        state.currentLink = `${item.href}`;
        state.currentLinkTitle = item.dataset('html') || item.html;
        return false;
      }
      state.currentLink = `${SEARCH_URL_PART}${combinedPopularHistory.value[count].PHRASE}`;
      state.currentLinkTitle = `${combinedPopularHistory.value[count].PHRASE}`;
    };

    if (e === 'down') {
      if (state.currentFocusEl < elements.length) {
        elements.forEach((item) => {
          item.classList.remove('_hover');
        });
        elements[state.currentFocusEl].classList.add('_hover');
        getItem(state.currentFocusEl);
        state.currentFocusEl += 1;
      }
    }

    if (e === 'up') {
      if (state.currentFocusEl > 1) {
        state.currentFocusEl -= 1;
        elements.forEach((item) => {
          item.classList.remove('_hover');
        });
        elements[state.currentFocusEl - 1].classList.add('_hover');
        getItem(state.currentFocusEl - 1);
      }
    }
  };

  const handlerDown = () => {
    moveResults('down');
  };

  const handlerUp = () => {
    moveResults('up');
  };

  /**
   * получаем данные истории поиска с localStorage
   */
  const getHistory = () => {
    const historyParams = ParamsLocalStorage.getLocalItem(SEARCH_HISTORY);
    state.history = historyParams || [];
  };

  /**
   * добавляем данные истории поиска в localStorage
   */
  const setHistory = (setHistoryArr = null) => {
    ParamsLocalStorage.setLocalItem(SEARCH_HISTORY, setHistoryArr || state.history);
  };

  /**
   * добавляем и кропаем список Истории в localStorage
   * @param value
   */
  const addToHistoryLocalStorage = (value) => {
    getHistory();
    const formattedValue = value.toLowerCase();
    if (!state.history.includes(formattedValue)) {
      const setHistoryArr = [...state.history];
      if (setHistoryArr.length > 3) setHistoryArr.pop();
      setHistoryArr.unshift(formattedValue);
      setHistory(setHistoryArr);
    }
  };

  /**
   * очищаем историю поиска в localStorage
   */
  const clearHistory = (key = null) => {
    state.disableHideSuggestions = true;
    if (key === null) {
      state.history = [];
    } else {
      state.history.splice(key, 1);
    }
    setHistory();
  };

  /**
   * добавляем в Историю поиска value по клику на ссылку автокомплитов
   * @param value
   */
  const setCurrentLinkTitle = (value) => {
    state.currentLinkTitle = value;
    addToHistoryLocalStorage(state.currentLinkTitle);
  };

  const submitHandler = () => {
    if (state.showSuggests && state.currentLink) {
      addToHistoryLocalStorage(state.currentLinkTitle);
      window.location = state.currentLink;
    } else if (trimQuery(state.query)) {
      addToHistoryLocalStorage(trimQuery(state.query));
      window.location = `/search/?q=${trimQuery(state.query)}`;
    }
  };

  const debouncedHideSuggests = debounce(() => {
    if (state.suggestsList.length < 1 && !state.showError
      && !state.currentLinkTitle && !state.disableHideSuggestions) {
      state.showSuggests = false;
    }
    if (state.disableHideSuggestions) {
      state.disableHideSuggestions = false;
      input.value.focus();
    }
  }, 200);

  const handlerBlur = () => {
    debouncedHideSuggests();
  };

  const handlerClear = () => {
    state.disableHideSuggestions = true;
    state.suggestsList = [];
    state.showError = false;
    input.value.focus();
  };

  const handlerClickOverlay = () => {
    const handler = () => {
      state.showSuggests = false;
      if (input.value && input.value.blur) {
        input.value.blur();
      }
    };

    const selectionText = window.getSelection().toString ? window.getSelection().toString() : '';

    if (!selectionText) {
      handler();
    }
  };

  /**
   * получаем список популярных фраз
   * @returns {Promise<void>}
   */
  const getPopularPhrases = async () => {
    try {
      const { data, success, message } = await apiRequest.get('/catalog/search/popular-phrase/');

      if (success && data.data) {
        state.popular.push(...data.data.map(phrase => phrase.attributes));
      } else if (message) {
        throw new Error(message);
      }
    } catch (e) {
      state.popular = [];
    }
  };

  const getSuggests = debounce(async () => {
    state.loading = true;
    state.currentFocusEl = 0;
    try {
      const { DATA, STATUS, MESSAGE } = await apiRequest.get('/catalog/search/suggests/', {
        q: trimQuery(state.query),
        limit_suggest: 5,
        limit_section: 3,
        limit_product: 5,
        limit_podborki: 0,
      });

      if (STATUS === 'OK') {
        state.suggestsList = DATA;
        state.showSuggests = true;
        state.showError = false;
      } else {
        throw new Error(MESSAGE);
      }
    } catch (e) {
      state.suggestsList = [];
      state.showSuggests = true;
      state.showError = true;
    } finally {
      state.loading = false;
    }
  }, 400);

  const checkExistParam = (query = '') => {
    const queryValue = typeof query === 'string' ? query : '';
    const urlParams = new URLSearchParams(window.location.search);
    const existingParam = urlParams.get('q');
    const existingParamValue = typeof existingParam === 'string' ? existingParam : '';
    return existingParamValue.toLowerCase() === queryValue.toLowerCase();
  };

  const handleInput = () => {
    if (state.query.length > 0) {
      state.loading = true;
      getSuggests();
    } else if (!state.query.length) {
      state.loading = false;
      state.isShowError = false;
      state.suggestsList = [];
    }
  };

  const handlerFocus = () => {
    handleInput();
    state.showSuggests = true;

    if (state.suggestsList.length < 1 && state.showError) {
      state.showError = false;
    }
  };

  watch(
    () => state.showSuggests,
    (value) => {
      state.currentFocusEl = 0;
      if (value) {
        opened();
        ParamsLocalStorage.setLocalItem('isSuggestingNow', String(1));
      } else {
        closed();
        ParamsLocalStorage.setLocalItem('isSuggestingNow', String(0));
      }
    },
  );

  watch(
    () => state.query,
    (value, oldValue) => {
      if (trimQuery(value) !== trimQuery(oldValue)) handleInput();
    },
  );

  return {
    ...toRefs(state),
    input,
    getSuggests,
    setCurrentLinkTitle,
    getHistory,
    getPopularPhrases,
    handleInput,
    handlerFocus,
    handlerBlur,
    handlerClear,
    handlerClickOverlay,
    checkExistParam,
    handlerDown,
    handlerUp,
    submitHandler,
    clearHistory,
  };
};
